/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

import JSONPretty from 'react-json-pretty';

import {
  Heading,
  Icons,
  Option,
  RangeDatePicker,
  Select,
} from '@sede-enterprise/shell-ds-react-framework';

import Table from '../Table';
import Button from '../../../../UI/Button';
import Loader from '../../../../UI/Loader';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { sendNotification } from '../../../../UI/Notification';
import { GET_DA_NOMS_AUDIT_LOG, MESSAGES } from '../../../../constants/FieldNominations';
import { get } from '../../../../services/GenesisApi';
import { UK_TIME_ZONE } from '../../../../constants/Common';
import { IDANomsAuditLogs } from '../../../../types/FieldNominations.type';
import { ENDUR_STATUS_MAP } from 'src/constants/BeachSwaps';
import Card from '../../../../UI/Card';
import { Col, FormRow, Row } from '../../../../UI/Grid';
import { DANomsAuditWrapper } from '../style';
import { getCurrentGasDay } from 'src/utils/common/utils';
import DATEFORMAT from 'src/utils/dateFormat';
import { rolePermissions } from 'src/constants/Permission';
import UploadDANoms from '../UploadDANoms';
import { IFullState } from 'src/types/All.type';

dayjs.extend(weekday);
dayjs.extend(utc);
dayjs.extend(localeData);

interface IDateRange {
  fromDate: string;
  toDate: string;
}

const { InfoCircle, ArrowLeft } = Icons;

const DANomsAuditLogs: FC = () => {
  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);
  const [openDANomsUpload, setOpenDANomsUpload] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [auditLogs, setAuditLogs] = useState<IDANomsAuditLogs[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<string>('WD');
  const [{ fromDate, toDate }, setDateRange] = useState<IDateRange>({
    fromDate: getCurrentGasDay().format(DATEFORMAT.yearMonthDay),
    toDate: getCurrentGasDay().add(1, 'day').format(DATEFORMAT.yearMonthDay),
  });
  const navigate = useNavigate();
  const location = useLocation();

  const getAuditLogs = () => {
    setLoading(true);
    let params;
    if (selectedFilter === 'DR') {
      params = new URLSearchParams({
        fromDate: fromDate ? dayjs(fromDate).format(DATEFORMAT.yearMonthDay) : '',
        toDate: toDate ? dayjs(toDate).format(DATEFORMAT.yearMonthDay) : '',
      });
    } else if (selectedFilter === 'WD') {
      params = new URLSearchParams({
        fromDate: getCurrentGasDay().format(DATEFORMAT.yearMonthDay),
        toDate: getCurrentGasDay().format(DATEFORMAT.yearMonthDay),
      });
    } else if (selectedFilter === 'DA') {
      params = new URLSearchParams({
        fromDate: getCurrentGasDay().add(1, 'days').format(DATEFORMAT.yearMonthDay),
        toDate: getCurrentGasDay().add(1, 'days').format(DATEFORMAT.yearMonthDay),
      });
    }

    get(`${GET_DA_NOMS_AUDIT_LOG}?${params.toString()}`)
      .then((response) => {
        const data = response.data ?? [];
        setAuditLogs(
          data.map((log) => ({
            ...log,
            expanded: false,
          })),
        );
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: MESSAGES.SERVER_ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getAuditLogs();
  }, [selectedFilter, fromDate, toDate]);

  const handleOnChangeFilterType = (value: string) => {
    setSelectedFilter(value);
    if (value === 'DR') {
      setDateRange(() => ({
        fromDate: getCurrentGasDay().format(DATEFORMAT.yearMonthDay),
        toDate: getCurrentGasDay().add(1, 'days').format(DATEFORMAT.yearMonthDay),
      }));
    }
  };

  const handleOnChangeDateRange = (value) => {
    setDateRange(() => ({
      fromDate: dayjs(value[0]).format(DATEFORMAT.yearMonthDay),
      toDate: dayjs(value[1]).format(DATEFORMAT.yearMonthDay),
    }));
  };

  const handlePayloadInfoClick = async (item: IDANomsAuditLogs) => {
    // Toggle expanded state
    const newExpanded = !item.expanded;

    setAuditLogs((prevLogs) =>
      prevLogs.map((log) =>
        log.id === item.id
          ? {
              ...log,
              expanded: newExpanded,
            }
          : log,
      ),
    );
  };

  const renderPayloadContent = (item: IDANomsAuditLogs) => {
    return (
      <JSONPretty
        id='json-pretty'
        data={item.payload}
        theme={{
          main: 'line-height:1.3;color:#66d9ef;background:#2d3748;overflow:auto;',
          error: 'line-height:1.3;color:#66d9ef;background:#2d3748;overflow:auto;',
          key: 'color:#f92672;',
          string: 'color:#a6e22e;',
          value: 'color:#ae81ff;',
          boolean: 'color:#ac80ff;',
        }}
      />
    );
  };

  const renderMessage = (item: IDANomsAuditLogs) => {
    return (
      <div>
        <div className='note-with-payload'>
          <span>{item.notes}</span>
          {item.payload && (
            <Button
              size='small'
              className='payload-info'
              icon={<InfoCircle />}
              title='View Payload'
              onClick={() => handlePayloadInfoClick(item)}
              iconOnly
              data-testid={`view-payload-${item.id}`}
            />
          )}
        </div>

        {item.expanded && (
          <div className='payload-section'>
            <div className='payload-header'>
              <span className='payload-data-header'>Payload Data:</span>
              <Button
                size='small'
                variant='outlined'
                className='payload-info'
                onClick={() => handlePayloadInfoClick(item)}
              >
                {item.expanded ? 'Hide' : 'Show'} Payload
              </Button>
            </div>

            <div className='payload-content'>{renderPayloadContent(item)}</div>
          </div>
        )}
      </div>
    );
  };

  const renderStatus = (item: IDANomsAuditLogs) => {
    // check if statusId is present in ENDUR_STATUS_MAP, if not return empty string
    const status = ENDUR_STATUS_MAP[item.statusId];
    if (status === 'Sent') {
      return <span className='status-sent'>Sent</span>;
    }
    if (status === 'Success') {
      return <span className='status-success'>Success</span>;
    }
    if (status === 'Failed') {
      return <span className='status-failure'>Failed</span>;
    }

    return <span className='status-pending'>{status}</span>;
  };

  const renderDate = (value: string) => (
    <div>{dayjs(value).format('ddd DD-MMM-YY HH:mm:ss A')}</div>
  );

  const columns = [
    {
      accessorKey: 'gasDay',
      header: 'Gas Day',
      cell: ({ getValue }: { getValue: () => string }) => dayjs(getValue()).format('DD-MMM-YYYY'),
    },
    {
      accessorKey: 'deal_DealLegId',
      header: 'Deal ID',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: { row: { original: IDANomsAuditLogs } }) => renderStatus(row.original),
    },
    {
      accessorKey: 'note',
      header: 'Message',
      cell: ({ row }: { row: { original: IDANomsAuditLogs } }) => renderMessage(row.original),
    },
    {
      accessorKey: 'comment',
      header: 'Comments',
    },
    {
      accessorKey: 'createdDateTime',
      header: 'Timestamp',
      cell: ({ getValue }: { getValue: () => string }) => renderDate(getValue()),
    },
  ];

  return (
    <DANomsAuditWrapper>
      <Card>
        <Row>
          <Col size={12}>
            <FormRow>
              <Col size={12} className='header-with-close'>
                <Button
                  className='close-mapping'
                  iconOnly
                  icon={<ArrowLeft />}
                  onClick={() => navigate(`/field-noms/field-nominations${location.search}`)}
                  variant='transparent'
                  data-testid='btn-back'
                />
                <Heading type='h2'>Day Ahead Nominations Info and Audit Logs</Heading>
              </Col>
            </FormRow>
            <FormRow className='filter-container'>
              <Col size={6} className='filters'>
                <div className='select-filter'>
                  <Select
                    allowClear={false}
                    value={selectedFilter}
                    onChange={handleOnChangeFilterType}
                    disabled={loading}
                    data-testid='select-filter-type'
                  >
                    <Option value='DR' data-testid='option-date-range'>
                      Date Range
                    </Option>
                    <Option value='WD' data-testid='option-within-day'>
                      Within Day
                    </Option>
                    <Option value='DA' data-testid='option-day-ahead'>
                      Day Ahead
                    </Option>
                  </Select>
                </div>

                {selectedFilter === 'DR' && (
                  <div className='date-range'>
                    <RangeDatePicker
                      placeholder={['From Date', 'To Date']}
                      allowClear={false}
                      value={[dayjs(fromDate), dayjs(toDate)]}
                      onChange={handleOnChangeDateRange}
                    />
                  </div>
                )}
              </Col>
              <Col size={6} className='button-wrapper'>
                <Button
                  size='small'
                  onClick={() => setOpenDANomsUpload(true)}
                  disabled={!permissions.includes(rolePermissions.uploadUkNomination) || loading}
                  data-testid='manual-upload-btn'
                >
                  Manual Upload DA Noms
                </Button>
              </Col>
            </FormRow>
            <FormRow className='table-wrapper'>
              {loading ? (
                <div className='loader'>
                  <Loader />
                </div>
              ) : (
                <Table
                  columns={columns}
                  data={auditLogs}
                  pagination={false}
                  maxHeight={300}
                  totalRecords={auditLogs.length}
                />
              )}
            </FormRow>
          </Col>
        </Row>
      </Card>
      {openDANomsUpload && (
        <UploadDANoms
          open={openDANomsUpload}
          onClose={() => setOpenDANomsUpload(false)}
          fromDate={fromDate}
          toDate={toDate}
          handleRefreshData={getAuditLogs}
        />
      )}
    </DANomsAuditWrapper>
  );
};

export default DANomsAuditLogs;
